'use client';

import { createContext, useContext, type CSSProperties, type ReactNode } from 'react';

/**
 * The uniform content scale of the desktop analysis window a subtree is rendered
 * in, or `null` when it is not inside one (the full-pane tab view).
 *
 * Private: publishing it is {@link ScaledContent}'s job, and the two halves of
 * that contract — the provider and the `zoom` — have to travel together.
 */
const ContentScaleContext = createContext<number | null>(null);

export function useContentScale(): number | null {
  return useContext(ContentScaleContext);
}

/**
 * Scales a subtree by `scale` and publishes it to descendants.
 *
 * `zoom` rather than `transform: scale` because it reflows: glyphs and strokes
 * re-render at the new size instead of being resampled, and it participates in
 * layout, so content stays inside an `overflow: hidden` ancestor. Percentages
 * resolve against the parent divided by the zoom, so this box lays out at
 * width/scale × height/scale — the smallest window hands a viewer MORE layout
 * room than the window itself, not less, which is why the windows need no
 * content floor below their size minima.
 *
 * The flex column is the analysis window's own layout; with one consumer that is
 * a fair trade for keeping both halves of the contract in one place. A provider
 * without the `zoom`, or a `zoom` without the provider, would silently break
 * every {@link UnscaledIsland} beneath it.
 */
export function ScaledContent({ scale, children }: { scale: number; children: ReactNode }) {
  return (
    <ContentScaleContext.Provider value={scale}>
      <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', zoom: scale }}>
        {children}
      </div>
    </ContentScaleContext.Provider>
  );
}

/**
 * Clears the published scale for a subtree portalled OUT of a window's zoomed
 * box (a floating dialog on `document.body`). Context crosses portals but the
 * `zoom` does not, so without this a chart inside would compensate for a zoom
 * it isn't under — drawn off-size, with every click landing off its points.
 */
export function OutsideScaledContent({ children }: { children: ReactNode }) {
  return <ContentScaleContext.Provider value={null}>{children}</ContentScaleContext.Provider>;
}

/**
 * Cancels the content zoom for a subtree that does its own pixel math.
 *
 * Anything that mixes visual pixels (`getBoundingClientRect`, `clientX`) with
 * layout pixels (CSS lengths, SVG attributes) reads wrong by the scale factor
 * inside a zoomed box — plotly.js pointer handling is the headline case, since
 * its compensation only inspects `transform` and never sees `zoom`. Nested zooms
 * multiply, so `1 / scale` here brings the subtree's effective zoom to exactly 1
 * and its measurements agree again. Such a subtree must be handed the scale
 * explicitly if its contents are to grow.
 *
 * The wrapper is unconditional inside a window, including at scale 1: swapping
 * between a fragment and an element changes the React tree shape, which remounts
 * the subtree every time a drag crosses the reference size — losing Plotly's
 * chart zoom and 3D camera. `zoom: 1` is inert; a changing tree shape is not.
 */
export function UnscaledIsland({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  const scale = useContentScale();
  if (scale === null) return <>{children}</>;
  return (
    <div style={{ width: '100%', height: '100%', ...style, zoom: 1 / scale }}>
      {children}
    </div>
  );
}
